/**
 * A playlist's cover image: `playlists/{playlist id}/cover.jpg` in the public `artwork` bucket.
 *
 * That file is overwritten in place whenever the playlist's cover changes (CreatePlaylistDialog,
 * PlaylistFullView), and the playlist stores its public URL. So two playlists must never point at
 * the same file: one that named another's cover would change whenever that one's did, possibly to
 * the artwork of another client's link. A copy of a playlist gets a file of its own.
 */
import { supabase } from "@/lib/supabase";

const COVER_BUCKET = "artwork";

/** An `artwork` object's URL on Storage: public, signed, authenticated or resized. */
const ARTWORK_OBJECT_PATH =
  /^\/storage\/v1\/(?:object|render\/image)\/(?:public|sign|authenticated)\/artwork\/(.+)$/;

/** Where playlist `playlistId`'s cover lives in `artwork`. */
export function playlistCoverPath(playlistId: string): string {
  return `playlists/${playlistId}/cover.jpg`;
}

/** The `artwork` object a URL on this project's Storage names, decoded, or null for any other URL. */
export function artworkPathFromUrl(url: string): string | null {
  try {
    const project = new URL(import.meta.env.VITE_SUPABASE_URL);
    const parsed = new URL(url.trim());
    if (parsed.origin !== project.origin) return null;
    const match = ARTWORK_OBJECT_PATH.exec(parsed.pathname);
    return match ? decodeURIComponent(match[1]) || null : null;
  } catch {
    return null;
  }
}

/**
 * The cover for `copyId`, a new copy of a playlist whose cover is at `coverUrl`.
 *
 * - A cover in this project's `artwork` bucket is copied, inside Storage, to the copy's own
 *   `playlists/{copyId}/cover.jpg`, and that file's URL is returned. The copy's row must exist
 *   first: Storage lets only a playlist's owner write its cover (20261012040000, N2).
 * - Any other URL is returned as it is, since nothing in Zen overwrites it.
 * - Null when the copy fails: the copy then has no cover, never the original's file. Never throws.
 */
export async function copyPlaylistCover(coverUrl: string, copyId: string): Promise<string | null> {
  const sourcePath = artworkPathFromUrl(coverUrl);
  if (sourcePath === null) return coverUrl;

  const coverPath = playlistCoverPath(copyId);
  try {
    const { error } = await supabase.storage.from(COVER_BUCKET).copy(sourcePath, coverPath);
    if (error) {
      console.warn("Couldn't copy the playlist's cover:", error.message);
      return null;
    }
    const { data } = supabase.storage.from(COVER_BUCKET).getPublicUrl(coverPath);
    // The cache-buster every cover upload adds, so no browser shows a file it cached at this path.
    return data?.publicUrl ? `${data.publicUrl}?t=${Date.now()}` : null;
  } catch (error) {
    console.warn("Couldn't copy the playlist's cover:", error);
    return null;
  }
}
